import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import FeedbackDialog from "../components/FeedbackDialog";
import OnboardingTour from "../components/OnboardingTour";
import BackButtonGuard from "../components/BackButtonGuard";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Atom, Beaker, Clock, Crown, ArrowRight, BookOpen, ChevronRight, Search, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

function daysLeft(iso) {
  if (!iso) return 0;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export default function Dashboard() {
  const { user } = useAuth();
  const [cls, setCls] = useState(user?.student_class || "X");
  const [subject, setSubject] = useState("physics");
  const [chapters, setChapters] = useState([]);
  const [allChapters, setAllChapters] = useState([]); // for global search
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get(`/syllabus/${cls}/${subject}`).then((r) => setChapters(r.data)).catch(() => setChapters([]));
  }, [cls, subject]);

  // Fetch flat list of every chapter across both classes and subjects for global search.
  useEffect(() => {
    const combos = [
      ["IX", "physics"], ["IX", "chemistry"], ["X", "physics"], ["X", "chemistry"],
    ];
    Promise.all(
      combos.map(([c, s]) =>
        api.get(`/syllabus/${c}/${s}`)
          .then((r) => r.data.map((ch) => ({ ...ch, cls: c, subject: s })))
          .catch(() => [])
      )
    ).then((lists) => setAllChapters(lists.flat()));
  }, []);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return allChapters.filter((ch) => {
      const hay = [
        ch.title,
        ...(ch.topics || []),
        `class ${ch.cls}`,
        ch.subject,
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [query, allChapters]);

  const dl = daysLeft(user?.subscription_expiry);
  const trialActive = user?.subscription_plan === "trial" && dl > 0;

  return (
    <div className="min-h-screen">
      <Header />
      <FeedbackDialog />
      <OnboardingTour />
      <BackButtonGuard />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        {/* Header row */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-neutral-600 mb-1">Dashboard</div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Hi {user?.name?.split(" ")[0]} 👋</h1>
            <p className="mt-1 text-neutral-700">Pick a chapter and jump into notes, chat, quiz or mock test.</p>
          </div>
          <div className="brut-card p-4 bg-[#FFF5D1] flex items-center gap-3">
            <Clock size={20} strokeWidth={2.5} />
            <div>
              <div className="text-xs font-bold uppercase tracking-widest">
                {user?.role === "owner" ? "Owner Access" : trialActive ? "Free Trial" : "Subscription"}
              </div>
              <div className="text-lg font-black">
                {user?.role === "owner" ? "Unlimited" : dl > 0 ? `${dl} days left` : "Expired"}
              </div>
            </div>
            {user?.role !== "owner" && (
              <Link to="/subscribe">
                <Button className="brut-btn rounded-none bg-[#1A1A1A] text-white hover:bg-[#1A1A1A]" data-testid="upgrade-btn">
                  <Crown size={14} className="mr-1" strokeWidth={2.5}/>Upgrade
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Selectors */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div className="brut-card p-4 bg-white">
            <div className="text-xs font-bold uppercase tracking-widest mb-2">Class</div>
            <div className="flex gap-2">
              {["IX", "X"].map((c) => (
                <button key={c} data-testid={`class-${c}`} onClick={() => setCls(c)}
                  className={`flex-1 brut-border brut-shadow-sm py-3 font-bold transition-transform ${cls === c ? "bg-[#FFB3BA]" : "bg-white hover:-translate-y-0.5"}`}>
                  Class {c}
                </button>
              ))}
            </div>
          </div>
          <div className="brut-card p-4 bg-white">
            <div className="text-xs font-bold uppercase tracking-widest mb-2">Subject</div>
            <div className="flex gap-2">
              {[{k:"physics", n:"Physics", i:Atom, bg:"bg-[#E9E6FA]"}, {k:"chemistry", n:"Chemistry", i:Beaker, bg:"bg-[#E8F3EC]"}].map((s) => (
                <button key={s.k} data-testid={`subject-${s.k}`} onClick={() => setSubject(s.k)}
                  className={`flex-1 flex items-center justify-center gap-2 brut-border brut-shadow-sm py-3 font-bold transition-transform ${subject === s.k ? s.bg : "bg-white hover:-translate-y-0.5"}`}>
                  <s.i size={18} strokeWidth={2.5}/>{s.n}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mock test link */}
        <div className="mb-8">
          <Link to={`/mock-test/${cls}/${subject}`} data-testid="mock-test-link">
            <div className="brut-card p-5 bg-[#FFB3BA] flex items-center justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest">Full Mock Test</div>
                <div className="text-xl font-black">Class {cls} {subject === "physics" ? "Physics" : "Chemistry"} — 15 Qs</div>
              </div>
              <ArrowRight size={24} strokeWidth={2.5}/>
            </div>
          </Link>
        </div>

        {/* Search across all chapters (Class IX + X, Physics + Chemistry) */}
        <div className="brut-card p-3 bg-white mb-6 flex items-center gap-2">
          <Search size={18} strokeWidth={2.5} className="ml-1 flex-none"/>
          <Input
            data-testid="chapter-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search topics or chapters (e.g. Light, Atoms, Newton's laws)…"
            className="brut-input rounded-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none flex-1"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid="chapter-search-clear"
              aria-label="Clear search"
              className="w-8 h-8 brut-border bg-white flex items-center justify-center hover:bg-neutral-100">
              <X size={14} strokeWidth={2.5}/>
            </button>
          )}
        </div>

        {searchResults !== null ? (
          <div data-testid="search-results">
            <h2 className="text-2xl font-extrabold mb-4">
              {searchResults.length === 0
                ? `No chapters match "${query}"`
                : `${searchResults.length} result${searchResults.length === 1 ? "" : "s"} for "${query}"`}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {searchResults.map((ch, i) => (
                <Link
                  key={`${ch.cls}-${ch.subject}-${ch.id}`}
                  to={`/chapter/${ch.cls}/${ch.subject}/${ch.id}`}
                  data-testid={`search-result-${ch.id}`}>
                  <div className={`brut-card p-6 h-full ${["bg-white","bg-[#FFF5D1]","bg-[#E9E6FA]","bg-[#E8F3EC]"][i%4]}`}>
                    <div className="flex items-start justify-between">
                      <div className="w-9 h-9 brut-border bg-white flex items-center justify-center font-black">
                        {ch.num}
                      </div>
                      <div className="text-[10px] font-bold tracking-widest uppercase text-neutral-600">
                        Class {ch.cls} · {ch.subject === "physics" ? "Physics" : "Chemistry"}
                      </div>
                    </div>
                    <h3 className="mt-4 text-xl font-bold leading-tight">{ch.title}</h3>
                    <ul className="mt-3 space-y-1 text-sm text-neutral-800">
                      {(ch.topics || []).slice(0, 3).map((t, j) => (
                        <li key={j} className="flex items-start gap-1">
                          <ChevronRight size={14} strokeWidth={2.5} className="mt-1 flex-none"/>
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline">
                      Open chapter <ArrowRight size={14} strokeWidth={2.5}/>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Chapters grid */}
            <h2 className="text-2xl font-extrabold mb-4">Chapters</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {chapters.map((ch, i) => (
                <Link key={ch.id} to={`/chapter/${cls}/${subject}/${ch.id}`} data-testid={`chapter-${ch.id}`}>
                  <div className={`brut-card p-6 h-full ${["bg-white","bg-[#FFF5D1]","bg-[#E9E6FA]","bg-[#E8F3EC]"][i%4]}`}>
                    <div className="flex items-start justify-between">
                      <div className="w-9 h-9 brut-border bg-white flex items-center justify-center font-black">
                        {ch.num}
                      </div>
                      <BookOpen size={18} strokeWidth={2.5}/>
                    </div>
                    <h3 className="mt-4 text-xl font-bold leading-tight">{ch.title}</h3>
                    <ul className="mt-3 space-y-1 text-sm text-neutral-800">
                      {ch.topics.slice(0, 3).map((t, j) => (
                        <li key={j} className="flex items-start gap-1">
                          <ChevronRight size={14} strokeWidth={2.5} className="mt-1 flex-none"/>
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline">
                      Open chapter <ArrowRight size={14} strokeWidth={2.5}/>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
